import { Link } from "react-router-dom";
import axios from "axios";
import { logNavigationClicked, AFFORDANCE } from "./analytics";
import { EVENT_BUS_EVENTS } from "./useEventBus";
import Epic from "../utils/epic";
import qs from "qs";
import ApiService from "./ApiService";

const openInNewTab = (url) => {
  const epic = new Epic();
  // if we are in Epic EHR we want to open the
  // link using Epic's external window interface
  if (epic.isEpic()) {
    epic.openExternalWindow(url);
  } else {
    // if we are not in Epic, just open in a new tab
    window.open(url, "_blank");
  }
};

export const navFunctions = {
  launchAssistant: (args, { eventBus }) => {
    if (eventBus) {
      eventBus.emit(EVENT_BUS_EVENTS.LAUNCH_ASSISTANT, null, args);
    } else {
      if (window?.DD_RUM) {
        window.DD_RUM.addError(
          "Event bus has not been passed to navigation helper",
          {
            scope: "mfe-container",
            team: "cd.ghostriders",
            component: "NavMenu"
          }
        );
      }
    }
  },
  mmxHandler: (args) => {
    const { redirectTo } = args;
    return axios.get(redirectTo).then((response) => {
      openInNewTab(response.data.redirect);
    });
  },
  isabelHandler: async (_args, { route }) => {
    const [baseUrl, originalQuery] = route.split("?");
    const originalParams = qs.parse(originalQuery);
    const isabelParams = await ApiService.getIsabelParams();
    const newQuery = qs.stringify(
      { ...originalParams, ...isabelParams },
      { addQueryPrefix: true }
    );
    const newUrl = baseUrl + newQuery;

    openInNewTab(newUrl);
  }
};
const hasDatadogRUM = () => typeof window !== "undefined" && window.DD_RUM;

const handleNonPrimaryClickOnNeoFaxPeds = (isNeoFaxPedsLink, linkProps) => {
  if (isNeoFaxPedsLink) {
    linkProps.onAuxClick = (e) => {
      e.preventDefault();
    };
  }
};

// Generate the props to pass to HamburgerLink and Navigation.Link
export const getLinkProps = (link, navLinks, pathname, options) => {
  const linkProps = {};
  const currentLocation = findCurrentLocation(navLinks, pathname);

  // nav links are react-router-dom Links by default
  linkProps.linkComponent = Link;
  linkProps.to = link.route ? link.route : "#";

  linkProps.onClick = () => {
    logNavigationClicked({
      affordance: AFFORDANCE.mainMenu,
      navigateTo: link.label,
      navigateFrom: currentLocation?.label
    });
  };

  // Check for function
  if (link.function) {
    const navFunction = navFunctions[link.function];
    const isNeoFaxPedsLink = link.id === "neofax";
    if (typeof navFunction === "function") {
      linkProps.onClick = (e) => {
        e.preventDefault();

        if (!(e.button === 1 && isNeoFaxPedsLink)) {
          navFunction(link.functionArgs, {
            eventBus: options?.eventBus,
            route: link.route
          });
          logNavigationClicked({
            affordance: AFFORDANCE.mainMenu,
            navigateTo: link.label,
            navigateFrom: currentLocation?.label
          });
        }
      };

      // prevents right click so user can't copy the link and open in a new window
      linkProps.onContextMenu = (e) => e.preventDefault();

      handleNonPrimaryClickOnNeoFaxPeds(isNeoFaxPedsLink, linkProps);
    } else {
      if (hasDatadogRUM()) {
        window.DD_RUM.addError(
          "Function passed for menu item, but not setup in code",
          {
            scope: "mfe-container",
            team: "cd.ghostriders",
            component: "NavMenu"
          }
        );
      }
    }
  }

  // check for options

  // external link uses a normal <a> for navigation (full page load)
  if (link.options?.useExternalLink) {
    linkProps.linkComponent = "a";
    linkProps.href = link.route;
    delete linkProps.to;
  }
  // open in new tab
  if (link.options?.openInNewTab) {
    linkProps.target = "_blank";
    linkProps.rel = link.options.needsReferrer
      ? "noopener"
      : "noreferrer noopener";
  }
  if (link.options?.hasCallToAction) {
    linkProps.onClick = (e) => {
      e.preventDefault();
      options.eventBus.emit(EVENT_BUS_EVENTS.LAUNCH_CTA);
    };
    // prevents right click so user can't copy the link and open in a new window
    linkProps.onContextMenu = (e) => e.preventDefault();
  }
  if (link.options?.htmlAttributes) {
    const { htmlAttributes } = link.options;
    Object.keys(htmlAttributes).forEach((attr) => {
      linkProps[attr] = htmlAttributes[attr];
    });
  }

  return linkProps;
};

export const findCurrentLocation = (links, pathname) => {
  for (const link of links) {
    if (link.route === pathname) {
      return link;
    }

    if (link.options?.dropdownItems) {
      const currentLocation = findCurrentLocation(
        link.options.dropdownItems,
        pathname
      );

      if (currentLocation) {
        return currentLocation;
      }
    }
  }

  return null;
};

export const isActive = (link, pathname) => {
  if (link.options && link.options.isDropdown) {
    return link.options.dropdownItems.some((dropdownItem) => {
      return dropdownItem.route === pathname;
    });
  }
  return pathname === link.route;
};

// Determine the index in which to display the current
// page indicator for hamburger menu and navigation menu
export const getIndicatorIndex = (links, pathname) => {
  let activeIndex = -1;
  links.forEach((link, index) => {
    if (isActive(link, pathname)) {
      activeIndex = index;
    }
  });
  return activeIndex;
};
